/**
 * Invite toasts and the Y / N hotkeys. Gang invites win over crew invites. The keys only exist while
 * a live invite is pending (so they never clutter the hint strip or steal Y / N otherwise).
 */
import type { GangInviteView } from '../../shared/messages/gangs';
import { touchUi } from '../input/touch';
import type { OnlineGame } from './game';
import { InviteTracker } from './inviteTracker';

/** How to answer an invite: the keys on a keyboard, the top of the book's index (ACCEPT / DECLINE) on touch. */
export function inviteAnswerHint(touch: boolean, kind: 'gang' | 'crew'): string {
  if (touch) return 'Open the BOOK: ACCEPT / DECLINE are at the top.';
  return kind === 'gang' ? 'Y to join, N to decline (or open the gang panel, G).' : 'Y to join, N to decline.';
}

/** Shared with the crew panel (its banner uses the same liveness rule as the keys). */
export const invites = new InviteTracker();

const RECHECK_S = 1;

export function registerInviteUi(game: OnlineGame): void {
  let offYes: (() => void) | null = null;
  let offNo: (() => void) | null = null;

  async function answer(accept: boolean): Promise<void> {
    const pick = invites.choose(game.now());
    if (!pick) return;
    if (pick.kind === 'gang') {
      invites.dismissGang(pick.id);
      await game.request('gangInviteReply', { invite: pick.id, accept }, { success: accept ? 'You are in. Welcome to the firm.' : undefined });
      // The answer carries our gang, not the invites list (joining voids the others; a stale one drops out).
      void game.request('gangInvites', {}, { quiet: true });
    } else {
      invites.dismissCrew();
      await game.request('crewReply', { crew: pick.id, accept }, { success: accept ? 'You joined the crew.' : undefined });
    }
    sync();
  }

  function sync(): void {
    const live = invites.choose(game.now()) !== null;
    if (live && !offYes) {
      // At the top of the STREET list while the invite is pending (on a phone that list is the answer).
      offNo = game.registerHotkey({ code: 'KeyN', label: 'Decline invite', first: true, run: () => void answer(false) });
      offYes = game.registerHotkey({ code: 'KeyY', label: 'Accept invite', first: true, run: () => void answer(true) });
    } else if (!live && offYes) {
      offYes();
      offNo?.();
      offYes = offNo = null;
    }
  }

  function announceGang(i: GangInviteView): void {
    game.toast(`${i.by} invited you to ${i.gang.name} [${i.gang.tag}] — ${inviteAnswerHint(touchUi.enabled, 'gang')}`, 'info', 10);
  }

  game.store.subscribe((keys) => {
    if (!keys.has('invites') && !keys.has('crewInvite')) return;
    const s = game.store.state;
    const fresh = invites.sync(s.invites, s.crewInvite, game.now());
    for (const i of fresh.gang) announceGang(i);
    if (fresh.crew) game.toast(`${fresh.crew.from} invites you to a crew — ${inviteAnswerHint(touchUi.enabled, 'crew')}`, 'info', 10);
    sync();
  });

  // Invites run out on their own: re-check once a second so a stale one releases Y / N.
  let t = 0;
  game.onFrame((dt) => {
    t += dt;
    if (t < RECHECK_S) return;
    t = 0;
    sync();
  });
}
